/**
 * payloadTransformer — the single, configuration-driven payload engine shared by
 * AddFormV1 and EditFormV1.
 *
 * Goal: the Admin form-group config is the ONLY source of truth for how a form
 * value becomes an API payload value. There is ZERO field-name / module-specific
 * branching here. Every behaviour is driven by these per-field config keys:
 *
 *   payloadKey      output key (dot-notation allowed). Default: field.field
 *   dataType        auto | string | number | boolean | array | object | date | custom
 *   elementType     for dataType "array": coerce each element (e.g. "number")
 *   payloadMode     auto | value | label | object | custom | template | skip
 *   valueKey        which key holds the id/value on an option object (default: value/id/_id…)
 *   displayKey      which key holds the label on an option object (default: label/name…)
 *   payloadTemplate object template for custom/template modes, with {{value}} {{label}} {{raw}} tokens
 *   defaultValue    value substituted when the form value is empty
 *   transformRule   { map: {...}, default, name } — value maps / named transforms
 *   omitEmpty       drop the key entirely when the final value is empty
 *
 * Nothing here knows about "priority", "recruiters", "noticePeriod", etc. Those
 * are expressed purely through the config above.
 *
 * Backward compatibility: when a field carries NONE of the new keys, the engine
 * falls back to the historical generic behaviour — dot-notation nesting, dayjs →
 * ISO string, scalar pass-through — so existing forms keep working unchanged.
 */

import dayjs from 'dayjs';

// ── small shared predicates ───────────────────────────────────────────────────

const isEmpty = (v) =>
  v === undefined ||
  v === null ||
  v === '' ||
  (Array.isArray(v) && v.length === 0);

const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v) && !isDayjs(v);

function isDayjs(v) {
  return (
    v &&
    typeof v === 'object' &&
    typeof v.format === 'function' &&
    typeof v.isValid === 'function'
  );
}

function isDateLike(v) {
  return isDayjs(v) || v instanceof Date;
}

function toISO(v) {
  if (v instanceof Date) return v.toISOString();
  if (isDayjs(v)) return v.isValid() ? v.toISOString() : null;
  return v;
}

// ── dot-notation get / set ────────────────────────────────────────────────────

export function getDeep(obj, path) {
  if (!path) return undefined;
  const parts = String(path).split('.');
  let cur = obj;
  for (const part of parts) {
    if (cur == null) return undefined;
    cur = cur[part];
  }
  return cur;
}

// readFormValue reads a field value tolerating BOTH antd conventions used in this
// codebase: a flat dotted key ("experience.from", as EditForm registers fields)
// and a nested object ("experience": { from }, as AddForm registers fields).
export function readFormValue(values, path) {
  if (values && Object.prototype.hasOwnProperty.call(values, path)) return values[path];
  return getDeep(values, path);
}

// hasFormValue reports whether a submit actually carried this field (so EditForm
// can leave untouched parts of the base record alone).
export function hasFormValue(values, path) {
  if (values && Object.prototype.hasOwnProperty.call(values, path)) return true;
  return getDeep(values, path) !== undefined;
}

export function setDeep(target, path, value) {
  const parts = String(path).split('.');
  let cur = target;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    if (!isPlainObject(cur[key])) cur[key] = {};
    cur = cur[key];
  }
  cur[parts[parts.length - 1]] = value;
  return target;
}

/** Merge a plain object's keys into target at root (used by custom/template spread). */
function mergeDeep(target, source) {
  Object.entries(source ?? {}).forEach(([k, v]) => {
    if (isPlainObject(v) && isPlainObject(target[k])) mergeDeep(target[k], v);
    else target[k] = v;
  });
  return target;
}

// ── option / value-label normalisation ────────────────────────────────────────

function optionLabelFor(field, value) {
  const opts = field.options ?? field.values ?? [];
  for (const o of opts) {
    if (typeof o === 'string') {
      if (o === value) return o;
    } else if ((o.value ?? o.id ?? o.name ?? o.label) === value) {
      return o.label ?? o.name ?? o.value;
    }
  }
  return undefined;
}

/**
 * splitValueLabel — normalises a raw form value into { value, label }.
 * Handles antd labelInValue ({value,label}), {id,name}/{_id,…} option objects,
 * and plain primitives (label recovered from field.options when present).
 */
function splitValueLabel(raw, field) {
  if (isPlainObject(raw)) {
    const value =
      (field.valueKey && raw[field.valueKey]) ??
      raw.value ??
      raw.id ??
      raw._id ??
      raw.key ??
      raw.code;
    const label =
      (field.displayKey && raw[field.displayKey]) ??
      raw.label ??
      raw.name ??
      raw.text ??
      raw.title ??
      value;
    return { value, label };
  }
  return { value: raw, label: optionLabelFor(field, raw) ?? raw };
}

// effectiveDataType — the dataType to apply. An explicit dataType always wins;
// otherwise it is inferred from the field's input type / multiplicity so that a
// plain `type: "number"` or a multi-select keeps coercing without the admin
// having to set dataType on every field (keeps existing configs working).
export function effectiveDataType(field = {}) {
  if (field.dataType && field.dataType !== 'auto') return field.dataType;
  if (field.multiSelect || field.mode === 'multiple') return 'array';
  switch (field.type) {
    case 'number':
      return 'number';
    case 'date':
    case 'time':
      return 'date';
    default:
      return 'auto';
  }
}

// ── data-type coercion ────────────────────────────────────────────────────────

export function coerceDataType(value, dataType, field = {}) {
  if (isDateLike(value)) {
    // Dates always serialise to ISO unless explicitly typed otherwise below.
    if (dataType === 'string') return toISO(value);
    if (dataType === 'number') {
      const t = isDayjs(value) ? value.valueOf() : value.getTime();
      return Number.isNaN(t) ? null : t;
    }
    if (dataType === 'date' || dataType === 'auto' || !dataType) return toISO(value);
  }

  switch (dataType) {
    case 'string':
      return value == null ? '' : String(value);

    case 'number': {
      if (isEmpty(value)) return null;
      const n = Number(value);
      return Number.isNaN(n) ? null : n;
    }

    case 'boolean':
      return value === true || value === 1 || value === '1' || value === 'true';

    case 'array': {
      let arr;
      if (Array.isArray(value)) arr = value;
      else if (isEmpty(value)) arr = [];
      else if (typeof value === 'string' && value.includes(','))
        arr = value.split(',').map((s) => s.trim()).filter(Boolean);
      else arr = [value];
      if (field.elementType) {
        return arr
          .map((el) => coerceDataType(el, field.elementType, {}))
          .filter((el) => el !== null && el !== undefined && el !== '');
      }
      return arr;
    }

    case 'object':
      return isPlainObject(value) ? value : value;

    case 'date':
      return toISO(value);

    case 'custom':
    case 'auto':
    case undefined:
    case '':
    default:
      return value;
  }
}

// ── transformRule (value maps + named transforms) ─────────────────────────────

const NAMED_TRANSFORMS = {
  firstChecked: (v) => (Array.isArray(v) ? v[0] : v),
  csvToArray: (v) =>
    typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v,
  arrayToCsv: (v) => (Array.isArray(v) ? v.join(',') : v),
  trim: (v) => (typeof v === 'string' ? v.trim() : v),
  upper: (v) => (typeof v === 'string' ? v.toUpperCase() : v),
  lower: (v) => (typeof v === 'string' ? v.toLowerCase() : v),
};

function applyTransformRule(value, transformRule) {
  if (!transformRule) return value;
  let rule = transformRule;
  if (typeof rule === 'string') {
    // Either a named transform or a JSON blob.
    if (NAMED_TRANSFORMS[rule]) return NAMED_TRANSFORMS[rule](value);
    try {
      rule = JSON.parse(rule);
    } catch {
      return value;
    }
  }

  let out = value;
  if (rule.name && NAMED_TRANSFORMS[rule.name]) out = NAMED_TRANSFORMS[rule.name](out);

  if (rule.map && typeof rule.map === 'object') {
    const key = Array.isArray(out) ? String(out[0]) : String(out);
    if (Object.prototype.hasOwnProperty.call(rule.map, key)) out = rule.map[key];
    else if (rule.default !== undefined) out = rule.default;
  }
  return out;
}

// ── template resolution (custom / template payload modes) ─────────────────────

const TOKEN_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

function resolveToken(token, ctx) {
  const [head, ...rest] = token.split('.');
  let base;
  if (head === 'value') base = ctx.value;
  else if (head === 'label') base = ctx.label;
  else if (head === 'raw') base = ctx.raw;
  else return undefined;
  return rest.length ? getDeep(base, rest.join('.')) : base;
}

function resolveTemplateNode(node, ctx) {
  if (typeof node === 'string') {
    // Whole-string single token → return the typed value (keep numbers numeric).
    const whole = node.match(/^\{\{\s*([\w.]+)\s*\}\}$/);
    if (whole) return resolveToken(whole[1], ctx);
    return node.replace(TOKEN_RE, (_, tok) => {
      const v = resolveToken(tok, ctx);
      return v == null ? '' : String(v);
    });
  }
  if (Array.isArray(node)) return node.map((n) => resolveTemplateNode(n, ctx));
  if (isPlainObject(node)) {
    const out = {};
    Object.entries(node).forEach(([k, v]) => {
      out[k] = resolveTemplateNode(v, ctx);
    });
    return out;
  }
  return node;
}

function parseTemplate(template) {
  if (!template) return null;
  if (typeof template === 'string') {
    try {
      return JSON.parse(template);
    } catch {
      return null;
    }
  }
  return template;
}

// ── payloadMode shaping ───────────────────────────────────────────────────────

function shapeSingle(field, raw) {
  const mode = field.payloadMode || 'auto';
  const dataType = effectiveDataType(field);
  const { value, label } = splitValueLabel(raw, field);

  switch (mode) {
    case 'value':
      return { kind: 'scalar', out: coerceDataType(value, dataType, field) };

    case 'label':
      return { kind: 'scalar', out: coerceDataType(label, field.dataType || 'string', field) };

    case 'object': {
      const vKey = field.valueKey || 'id';
      const dKey = field.displayKey || 'name';
      return {
        kind: 'scalar',
        out: {
          [vKey]: coerceDataType(value, field.elementType || 'auto', {}),
          [dKey]: label,
        },
      };
    }

    case 'custom':
    case 'template': {
      const tpl = parseTemplate(field.payloadTemplate);
      if (!tpl) return { kind: 'scalar', out: coerceDataType(value, dataType, field) };
      const ctx = { value: coerceDataType(value, field.elementType || 'auto', {}), label, raw };
      const resolved = resolveTemplateNode(tpl, ctx);
      // A template that yields a plain object spreads into the parent unless the
      // admin pinned an explicit payloadKey.
      return { kind: field.payloadKey ? 'scalar' : 'spread', out: resolved };
    }

    case 'auto':
    default:
      return { kind: 'scalar', out: coerceDataType(value, dataType, field) };
  }
}

/**
 * shapeFieldValue — turns one field's raw form value into its payload contribution.
 * Returns { kind: 'scalar'|'spread'|'skip', out }.
 *   scalar → write `out` at the field's payloadKey
 *   spread → merge `out` (a plain object) into the parent payload
 *   skip   → contribute nothing
 */
export function shapeFieldValue(field, rawValue) {
  if (field.payloadMode === 'skip') return { kind: 'skip' };

  let raw = rawValue;
  if (isEmpty(raw) && field.defaultValue !== undefined && field.defaultValue !== '') {
    raw = field.defaultValue;
  }

  raw = applyTransformRule(raw, field.transformRule);

  if (isEmpty(raw) && field.omitEmpty) return { kind: 'skip' };

  // Multi-value (multi-select / array source) with a per-element shape.
  const isMultiSource =
    Array.isArray(raw) &&
    (effectiveDataType(field) === 'array' || field.payloadMode === 'object');

  if (isMultiSource && field.payloadMode && field.payloadMode !== 'value' && field.payloadMode !== 'auto') {
    const out = raw.map((item) => shapeSingle(field, item).out);
    return { kind: 'scalar', out };
  }

  return shapeSingle(field, raw);
}

// ── field flattening from groups ──────────────────────────────────────────────

/** All leaf fields across groups, with their group context (for addRow arrays). */
export function flattenFields(groups = []) {
  const out = [];
  groups.forEach((g) => {
    (g.fields ?? []).forEach((f) => {
      out.push({ field: f, group: g });
    });
  });
  return out;
}

export function fileFields(groups = []) {
  return flattenFields(groups)
    .map(({ field }) => field)
    .filter((f) => f.type === 'file');
}

/**
 * collectFileParts — gather the NEW files the user picked, ready to append to a
 * multipart request, so create AND edit upload through the same gateway endpoint.
 *
 * Returns [{ formKey, file }]. The part name is the field's `fileKey` if set
 * (e.g. jobs reads "file"), else the first segment of its path
 * ("passport.passportUploadName" → "passport"). addRow groups fan out across
 * every row. Only fresh uploads (antd `originFileObj`, or a raw File/Blob) are
 * included — existing/stored files (url only) are left alone. Config-driven; no
 * field-name or module-specific logic.
 *
 * opts.indexed — when true, files inside an addRow group are emitted under an
 * INDEXED part name ("documents[0]", "documents[1]", …) where the index is the
 * row's position. EditForm uses this because the candidates update handler reads
 * document files by indexed key and aligns each file to its metadata row by
 * index.
 *
 * opts.scope — 'all' (default) | 'flat' | 'addRow'. Lets a caller collect only
 * the non-repeatable (flat) file fields, or only the repeatable (addRow) ones.
 * AddForm uploads flat files in the create request, then attaches addRow files
 * (e.g. candidate documents) in a follow-up update — the create handler for
 * repeatable document files is unreliable, while the indexed update path is the
 * proven one. Config-driven (keyed on the group's addRow flag), no module names.
 */
export function collectFileParts(values, groups = [], opts = {}) {
  const parts = [];
  const scope = opts.scope ?? 'all';
  const push = (formKey, node) => {
    if (node == null) return;
    const list = Array.isArray(node) ? node : [node];
    list.forEach((file) => {
      const raw = file?.originFileObj || file;
      if (typeof File !== 'undefined' && raw instanceof File) parts.push({ formKey, file: raw });
      else if (typeof Blob !== 'undefined' && raw instanceof Blob) parts.push({ formKey, file: raw });
    });
  };
  (groups ?? []).forEach((group) => {
    const files = (group.fields ?? []).filter((f) => f.type === 'file');
    if (!files.length) return;
    if (group.addRow) {
      if (scope === 'flat') return;
      const rows = values[group.name];
      if (!Array.isArray(rows)) return;
      files.forEach((field) => {
        const baseKey = field.fileKey ?? String(field.field).split('.')[0];
        rows.forEach((row, rowIdx) => {
          const formKey = opts.indexed ? `${baseKey}[${rowIdx}]` : baseKey;
          push(formKey, readFormValue(row, field.field));
        });
      });
      return;
    }
    if (scope === 'addRow') return;
    files.forEach((field) => {
      const formKey = field.fileKey ?? String(field.field).split('.')[0];
      push(formKey, readFormValue(values, field.field));
    });
  });
  return parts;
}

// ── the two public builders ───────────────────────────────────────────────────

/**
 * buildPayload — config-driven payload object from antd form values.
 *
 * @param values  the antd form values object (may contain dot-notation nesting,
 *                Form.List arrays for addRow groups, and dayjs date objects)
 * @param groups  normalised form groups (with the per-field payload config)
 * @param opts    { base } optional base object to merge onto (edit keeps the
 *                untouched parts of the original record)
 * @returns       a plain payload object ready to JSON.stringify
 *
 * File-typed fields are skipped — the caller handles uploads separately.
 */
export function buildPayload(values, groups, opts = {}) {
  const payload = isPlainObject(opts.base) ? structuredClone(opts.base) : {};

  // Strip file fields out of any base so stale file arrays never ride along.
  fileFields(groups).forEach((f) => {
    if (f.payloadKey || f.field) {
      // best-effort removal at both the configured key and source key
      deleteDeep(payload, f.payloadKey || f.field);
      deleteDeep(payload, f.field);
    }
  });

  (groups ?? []).forEach((group) => {
    const groupFields = group.fields ?? [];

    if (group.addRow) {
      // Repeatable group → ROW-oriented array of objects:
      //   [{ fieldA: v, fieldB: v }, { ... }]
      // matching a Go []struct (e.g. workExperience, educationDetails, documents).
      // The output key is the group's payloadKey (the struct's array key, e.g.
      // "workExperience") falling back to the group name.
      const rows = values[group.name];
      // Not present in this submit (e.g. EditForm doesn't prefill Form.List) →
      // leave whatever the base record already has untouched.
      if (!Array.isArray(rows)) return;
      const outKey = group.payloadKey || group.name;
      const rowObjects = rows
        .map((row) => buildRowObject(groupFields, row))
        .filter((obj) => obj && Object.keys(obj).length > 0);
      setDeep(payload, outKey, rowObjects);
      return;
    }

    groupFields.forEach((field) => {
      if (field.type === 'file') return; // uploaded separately

      if (!hasFormValue(values, field.field)) {
        // Field not present in this submit — leave base value untouched.
        return;
      }
      const raw = readFormValue(values, field.field);

      const result = shapeFieldValue(field, raw);
      if (result.kind === 'skip') return;
      if (result.kind === 'spread' && isPlainObject(result.out)) {
        mergeDeep(payload, result.out);
        return;
      }
      setDeep(payload, field.payloadKey || field.field, result.out);
    });
  });

  return payload;
}

// buildRowObject — shape one addRow row into a plain object keyed by each
// field's payloadKey/field (relative to the row). File fields are skipped (they
// upload separately and their metadata is merged server-side).
function buildRowObject(groupFields, row) {
  const obj = {};
  groupFields.forEach((field) => {
    if (field.type === 'file') return;
    if (!hasFormValue(row, field.field)) return;
    const raw = readFormValue(row, field.field);
    const result = shapeFieldValue(field, raw);
    if (result.kind === 'skip') return;
    if (result.kind === 'spread' && isPlainObject(result.out)) {
      mergeDeep(obj, result.out);
      return;
    }
    setDeep(obj, field.payloadKey || field.field, result.out);
  });
  return obj;
}

function deleteDeep(obj, path) {
  const parts = String(path).split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (!isPlainObject(cur[parts[i]])) return;
    cur = cur[parts[i]];
  }
  delete cur[parts[parts.length - 1]];
}

/**
 * buildInitialValue — reverse direction, for EditForm prefill.
 * Given a field config and the stored value (which may be a scalar, an
 * { id, value } reference, an { id, name } object, or an array of those),
 * returns the value the input control expects.
 *
 *   - date/time fields → dayjs
 *   - select/radio/checkbox → the id/value (single) or array of ids (multi)
 *   - everything else → the scalar
 *
 * This replaces convertInitialValue's hardcoded field-name handling.
 */
export function buildInitialValue(field, stored) {
  if (stored === undefined || stored === null) return stored;

  if (field.type === 'date' || field.type === 'time') {
    if (!stored || stored === '0001-01-01T00:00:00Z') return null;
    const p = dayjs(stored);
    return p.isValid() ? p : null;
  }

  const pickId = (item) => {
    if (isPlainObject(item)) {
      return (
        (field.valueKey && item[field.valueKey]) ??
        item.id ??
        item._id ??
        item.value ??
        item.userId ??
        item.recruiterId
      );
    }
    return item;
  };

  // Radio groups are ALWAYS single-select in the UI, even when the payload
  // dataType is "array" (e.g. jobRemoteStatus → []string). Prefill the scalar
  // so the selected radio shows; the payload engine re-wraps it to an array on
  // save via coerceDataType. Without this, a radio gets an array value and
  // renders with nothing selected.
  const isMulti = field.type === 'radio' ? false : effectiveDataType(field) === 'array';

  if (field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') {
    if (isMulti) {
      const arr = Array.isArray(stored) ? stored : [stored];
      return arr.map(pickId).filter((v) => v !== undefined && v !== null && v !== '');
    }
    if (Array.isArray(stored)) return pickId(stored[0]);
    return pickId(stored);
  }

  // checkbox group with a transformRule map (e.g. "high" → checked) is handled
  // by the caller via the same map; here we just pass the scalar through.
  return stored;
}

export default {
  buildPayload,
  buildInitialValue,
  shapeFieldValue,
  coerceDataType,
  getDeep,
  setDeep,
  flattenFields,
  fileFields,
};
